import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Badge } from './components/ui/badge';
import { Textarea } from './components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './components/ui/alert-dialog';
import { Plus, Edit, Trash2, Upload, Download, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const BookCategoryManagement = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  });
  const [bulkImportText, setBulkImportText] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/books/categories/all');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const resetForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      display_order: categories.length * 10,
      is_active: true
    });
  };

  const handleAddCategory = () => {
    resetForm();
    setEditingCategory(null);
    setShowAddDialog(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order,
      is_active: category.is_active
    });
    setEditingCategory(category);
    setShowAddDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(`/books/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated successfully!');
      } else {
        // Create new category
        await axios.post('/books/categories', categoryForm);
        toast.success('Category added successfully!');
      }
      
      setShowAddDialog(false);
      await fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save category';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async (category) => {
    try {
      await axios.delete(`/books/categories/${category.id}`);
      toast.success('Category deleted successfully!');
      setShowDeleteDialog(null);
      await fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete category';
      toast.error(errorMessage);
      setShowDeleteDialog(null);
    }
  };

  const handleMoveCategory = async (categoryId, direction) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    const newOrder = direction === 'up' ? category.display_order - 15 : category.display_order + 15;
    
    try {
      await axios.put(`/books/categories/${categoryId}`, {
        display_order: newOrder
      });
      await fetchCategories();
    } catch (error) {
      toast.error('Failed to reorder category');
    }
  };

  const handleBulkImport = async () => {
    const categoryNames = bulkImportText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (categoryNames.length === 0) {
      toast.error('Please enter category names, one per line');
      return;
    }

    try {
      const response = await axios.post('/books/categories/bulk-create', categoryNames);
      toast.success(response.data.message);
      if (response.data.skipped.length > 0) {
        toast.info(`Skipped ${response.data.skipped.length} existing categories`);
      }
      
      setBulkImportText('');
      setShowBulkImport(false);
      await fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to import categories';
      toast.error(errorMessage);
    }
  };

  const exportCategories = () => {
    const categoryNames = categories
      .filter(cat => cat.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .map(cat => cat.name)
      .join('\n');
    
    const blob = new Blob([categoryNames], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'book-categories.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Categories exported successfully!');
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Book Category Management</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Action Buttons */}
            <div className="flex gap-2 mb-4">
              <Button onClick={handleAddCategory} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Category
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowBulkImport(true)}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Bulk Import
              </Button>
              <Button 
                variant="outline" 
                onClick={exportCategories}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>

            {/* Categories Table */}
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories
                      .sort((a, b) => a.display_order - b.display_order)
                      .map((category) => (
                        <TableRow key={category.id}>
                          <TableCell className="font-medium">{category.name}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {category.description || '-'}
                          </TableCell>
                          <TableCell>{category.display_order}</TableCell>
                          <TableCell>
                            <Badge variant={category.is_active ? "default" : "secondary"}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveCategory(category.id, 'up')}
                                title="Move up"
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveCategory(category.id, 'down')}
                                title="Move down"
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditCategory(category)}
                                title="Edit category"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowDeleteDialog(category)}
                                title="Delete category"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={onClose} variant="outline">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="category-name">Category Name *</Label>
              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="e.g., Science Fiction"
              />
            </div>
            
            <div>
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Optional description for this category"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="display-order">Display Order</Label>
                <Input
                  id="display-order"
                  type="number"
                  value={categoryForm.display_order}
                  onChange={(e) => setCategoryForm({...categoryForm, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
              
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={categoryForm.is_active}
                  onChange={(e) => setCategoryForm({...categoryForm, is_active: e.target.checked})}
                />
                <Label htmlFor="is-active">Active</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={!categoryForm.name.trim()}>
              {editingCategory ? 'Update' : 'Create'} Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Import Categories</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="bulk-categories">Category Names (one per line)</Label>
            <Textarea
              id="bulk-categories"
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              placeholder="Science Fiction&#10;Art - Music - Cinema&#10;Children&#10;Novels"
              rows={10}
              className="mt-2"
            />
            <p className="text-sm text-gray-600 mt-2">
              Enter category names, one per line. Existing categories will be skipped.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImport(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkImport} disabled={!bulkImportText.trim()}>
              Import Categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <AlertDialog open={!!showDeleteDialog} onOpenChange={() => setShowDeleteDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Category</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the category "{showDeleteDialog.name}"? 
                This action cannot be undone. If any books are using this category, 
                you'll need to reassign them first.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowDeleteDialog(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleDeleteCategory(showDeleteDialog)}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default BookCategoryManagement;